import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import $ from 'jquery';
import { logError as reportParsingError } from 'remoteLogging';
import { AuthExtensions } from './authExtensions';
import { ButtonGenerator } from './buttonGenerator';
import { LabelGenerator } from './labelGenerator';

const EXTENSIONS_NAMESPACE = 'http://citrix.com/authentication/response/extensions/1';
const WEBVIEW_NAMESPACE = 'http://citrix.com/authentication/response/webview/1';

export type ParsedRequirementNode = ReturnType<
	typeof RequirementNodeParser['parseRequirementsNode']
>;

export const RequirementNodeProcessor = {
	addFieldsForEachRequirement(
		requirementsNodes: JQuery,
		form: JQuery,
		xmlData: Element,
		workspaceConfiguration: WorkspaceConfiguration
	) {
		let buttonsAddedCount = 0;
		let buttonStack: any = [];

		requirementsNodes.each(function (this: Element) {
			const requirementsNode = RequirementNodeParser.parseRequirementsNode(this);

			if (requirementsNode.input.button != null) {
				buttonStack.push(requirementsNode);
			} else {
				if (buttonStack.length > 0) {
					form.append(
						ButtonGenerator.generateButtons(buttonStack, xmlData, buttonsAddedCount < 1)
					);
					buttonsAddedCount += buttonStack.length;
					buttonStack = [];
				}

				// Auto post fields are restricted to being the only credential in the form.
				if (!AuthExtensions.isAutoPostAddedToForm()) {
					form.append(generateField(requirementsNode, workspaceConfiguration));
				} else {
					reportParsingError(
						'More than one credential in a form containing an auto-post credential is not allowed.'
					);
				}
			}
		});

		if (buttonStack.length > 0) {
			form.append(
				ButtonGenerator.generateButtons(buttonStack, xmlData, buttonsAddedCount < 1)
			);
			buttonsAddedCount += buttonStack.length;
		}

		// Add an auto post button to the end of the form if an auto post credential has been added.
		if (AuthExtensions.isAutoPostAddedToForm()) {
			form.append(AuthExtensions.getAutoPostButtonForForm());
		}

		$('input, select', form).each(function (this: Element) {
			$(this).change(function (this: Element) {
				$(this).addClass('editedCredential');
			});
		});
	},
};

function generateField(
	requirementsNode: ParsedRequirementNode,
	workspaceConfiguration: WorkspaceConfiguration
) {
	if (requirementsNode.input.button != null) {
		// buttons should have happened elsewhere
		reportParsingError('button should have been sorted elsewhere');
	}

	const fieldNode = $('<div/>')
		.addClass('field')
		.addClass('CredentialType' + requirementsNode.credential.type);

	InputFieldGenerator.addInputFieldOrLabel(
		fieldNode,
		requirementsNode,
		workspaceConfiguration
	);
	return fieldNode;
}

const RequirementNodeParser = {
	parseRequirementsNode(node: Element) {
		const credentialNode = $('Credential', node);
		if (credentialNode.length !== 1) {
			reportParsingError('could not parse Credential node');
		}
		const labelNode = $('Label', node);
		if (labelNode.length !== 1) {
			reportParsingError('could not parse Label node');
		}
		const inputNode = $('Input', node);
		if (inputNode.length !== 1) {
			reportParsingError('could not parse Input node');
		}
		return {
			credential: parseCredentialNode(credentialNode),
			label: parseLabelNode(labelNode),
			input: parseInputNode(inputNode),
		};
	},
};

function parseCredentialNode(credentialNode: JQuery) {
	const idNode = $('ID', credentialNode); // AuthManager ensures id is valid
	if (idNode.length > 1) {
		reportParsingError('could not parse credential id');
	}
	let id = null;
	if (idNode.length === 1) {
		id = idNode.text();
	}

	const typeNode = $('Type', credentialNode);
	if (typeNode.length !== 1) {
		reportParsingError('could not parse credential type');
	}
	// ignoring the SaveId for now

	// Parse any <wv:WebView> tags
	const webViewData = parseWebViewCredentialNameSpaceData(credentialNode);

	// Parse the <ext:data> tags
	const extData = parseCredentialExtensionData(credentialNode);
	return {
		id,
		type: typeNode.text(),
		data: extData,
		webview: webViewData,
	};
}

function parseCredentialExtensionData(credentialNode: any) {
	const itemList = {};
	const dataNode = findChildNodeWithNamespace(
		credentialNode,
		EXTENSIONS_NAMESPACE,
		'Data'
	);
	if (dataNode == null || dataNode.length === 0) {
		return itemList;
	}
	const items = findChildNodeWithNamespace(dataNode, EXTENSIONS_NAMESPACE, 'Item');
	items.each(function (this: Element) {
		try {
			const key = findChildNodeWithNamespace(this, EXTENSIONS_NAMESPACE, 'Key')
				.first()
				.text();
			const value = findChildNodeWithNamespace(this, EXTENSIONS_NAMESPACE, 'Value')
				.first()
				.text();
			if (!key || !value) {
				reportParsingError(
					'Item tag is invalid, both a key and a value are required: ' + xmlToString(this)
				);
			}
			itemList[key] = value;
		} catch (e) {
			reportParsingError('could not parse extension data item: ' + xmlToString(this));
		}
	});
	return itemList;
}

function parseWebViewCredentialNameSpaceData(credentialNode: JQuery) {
	let webViewData: {
		startUrl?: string;
		postData?: string;
		stateContext?: string;
		postbackUrl?: string;
	} = {};
	const webviewNode = findChildNodeWithNamespace(
		credentialNode,
		WEBVIEW_NAMESPACE,
		'WebView'
	);
	if (webviewNode == null || webviewNode.length === 0) {
		return webViewData;
	}
	try {
		// Parse the Start URL (required)
		const startUrl = findChildNodeWithNamespace(
			webviewNode,
			WEBVIEW_NAMESPACE,
			'StartUrl'
		);
		if (!startUrl || startUrl.length === 0) {
			// Parse error
			reportParsingError(
				'Required <StartUrl> tag not found in webView node: ' +
					xmlToString(webviewNode[0])
			);
		}
		// Parse the Post Data (optional)
		const postDataNode = findChildNodeWithNamespace(
			webviewNode,
			WEBVIEW_NAMESPACE,
			'PostData'
		);
		const postData =
			postDataNode == null || postDataNode.length === 0 ? '' : postDataNode.text();

		// Extract the higher-level form data (outside of the credential node) needed by the webview credential handler
		const topLevel = credentialNode.parents().last();
		const stateContextNode = topLevel.find('StateContext');
		const stateContext =
			stateContextNode == null || stateContextNode.length === 0
				? ''
				: stateContextNode.text();
		const postbackUrlNode = topLevel.find('PostBack');
		const postbackUrl =
			postbackUrlNode == null || postbackUrlNode.length === 0
				? ''
				: postbackUrlNode.text();

		webViewData = {
			startUrl: startUrl.text(),
			postData,
			stateContext,
			postbackUrl,
		};
	} catch (e) {
		reportParsingError(
			'could not parse webview data item: ' + xmlToString(webviewNode[0])
		);
	}
	return webViewData;
}

function xmlToString(xml: Element) {
	return new XMLSerializer().serializeToString(xml);
}

/*
 * Workaround to cover the modern browsers varying approaches for finding namespaced items with jQuery
 * (specifically Firefox requiring the explicit prefix vs. Chrome requiring no prefix)
 * and IE8 not supporting getElementsByTagNameNS
 */
function findChildNodeWithNamespace(
	parent: JQuery | Element,
	namespace: string,
	target: string
) {
	return $(parent)
		.children()
		.filter(function (this: Element & { baseName: string }) {
			if (this.namespaceURI === namespace) {
				const name = this.localName || this.baseName;
				return name === target;
			}
			return false;
		});
}

export interface LabelNode {
	type: string;
	src?: string;
	text?: string;
	highlightFields?: string[];
}

function parseLabelNode(labelNode: JQuery): LabelNode {
	const typeNode = $('Type', labelNode);
	if (typeNode.length !== 1) {
		reportParsingError('could not parse label type');
	}

	const type = typeNode.text();
	if (type === 'none' || type === '') {
		return {
			type: 'none',
		};
	} else if (type === 'image') {
		return {
			type,
			src: $('Binary', labelNode).text(),
		};
	} else if (
		type === 'plain' ||
		type === 'heading' ||
		type === 'information' ||
		type === 'warning' ||
		type === 'error' ||
		type === 'confirmation' ||
		type === 'inprogress'
	) {
		const textNode = $('Text', labelNode);
		if (textNode.length !== 1) {
			reportParsingError('could not find text for label');
		}
		const highlightFields: string[] = [];
		const fieldsToHighlightNodes = $('HighlightFields CredentialID', labelNode);
		fieldsToHighlightNodes.each(function (this: Element) {
			const id = $(this).text();
			highlightFields.push(id);
		});

		return {
			type,
			text: textNode.text(),
			highlightFields,
		};
	}

	reportParsingError('Unrecognised label type :' + type);
	return undefined;
}

function parseInputNode(inputNode: JQuery) {
	const assistiveTextNode = $('AssistiveText', inputNode);
	if (assistiveTextNode.length > 1) {
		reportParsingError('could not parse input AssistiveText');
	}
	let assistiveText = null;
	if (assistiveTextNode.length === 1) {
		assistiveText = assistiveTextNode.text();
	}

	const text = parseTextNode($('Text', inputNode));
	const radioButton = parseRadioButton($('RadioButton', inputNode));
	const comboBox = parseComboBox($('ComboBox', inputNode));
	const multiComboBox = parseMultiComboBox($('MultiComboBox', inputNode));
	const button = parseButton($('Button', inputNode));
	const checkBox = parseCheckBox($('CheckBox', inputNode));

	return {
		assistiveText,
		text,
		radioButton,
		comboBox,
		multiComboBox,
		button,
		checkBox,
	};
}

function parseTextNode(textNode: JQuery) {
	if (textNode.length !== 1) {
		return null;
	}
	let secret = false;
	const secretNodeText = $('Secret', textNode).text();
	if (secretNodeText === 'true' || secretNodeText === 'True') {
		secret = true;
	}

	let readOnly = false;
	const readOnlyNodeText = $('ReadOnly', textNode).text();
	if (readOnlyNodeText === 'true' || readOnlyNodeText === 'True') {
		readOnly = true;
	}

	const initialValue = $('InitialValue', textNode).text();

	const constraint = $('Constraint', textNode).text();
	const mustNotBeEmpty = constraint === '.+';

	return {
		secret,
		readOnly,
		initialValue,
		mustNotBeEmpty,
	};
}

function parseRadioButton(radioButtonNode: JQuery) {
	return parseComboBox(radioButtonNode);
}

function parseComboBox(comboBoxNode: JQuery) {
	if (comboBoxNode.length !== 1) {
		return null;
	}
	const initialSelectionNode = $('InitialSelection', comboBoxNode);
	let initalSelection = null;
	if (initialSelectionNode.length === 1) {
		initalSelection = initialSelectionNode.text();
	} else if (initialSelectionNode.length > 1) {
		reportParsingError('must only have one inital selection node');
	}

	const displayValues = getDisplayValues(comboBoxNode, false);

	return {
		initialSelection: initalSelection,
		displayValues,
	};
}

function parseMultiComboBox(multiComboBoxNode: JQuery) {
	if (multiComboBoxNode.length !== 1) {
		return null;
	}
	const displayValues = getDisplayValues(multiComboBoxNode, true);

	return {
		displayValues,
	};
}

function getDisplayValues(comboBoxNode: any, parseSelectNodes: boolean) {
	const displayValuesNode = $('DisplayValues', comboBoxNode);
	if (displayValuesNode.length !== 1) {
		reportParsingError('must have a displayValues node');
	}

	const displayValues: any[] = [];
	const displayValueNodes = $('DisplayValue', displayValuesNode);
	displayValueNodes.each(function (this: Element) {
		let selectNode;
		const displayValueNode = $(this);
		const displayNode = $('Display', displayValueNode);

		if (displayNode.length !== 1) {
			reportParsingError('Every DisplayValue node must have a Display node');
		}

		const valueNode = $('Value', displayValueNode);
		if (valueNode.length !== 1) {
			reportParsingError('Every DisplayValue node must have a Value node');
		}

		if (parseSelectNodes) {
			selectNode = $('Select', displayValueNode);
			if (selectNode.length > 1) {
				reportParsingError('Must have at most 1 select node in a DisplayValue node');
			}
		}

		let displayValue;
		if (parseSelectNodes) {
			displayValue = {
				display: displayNode.text(),
				value: valueNode.text(),
				selected: selectNode.text() === 'true' || selectNode.text() === 'True',
			};
		} else {
			displayValue = {
				display: displayNode.text(),
				value: valueNode.text(),
			};
		}

		displayValues.push(displayValue);
	});

	return displayValues;
}

function parseButton(buttonNode: JQuery) {
	if (buttonNode.length !== 1) {
		return null;
	}
	return buttonNode.text();
}

function parseCheckBox(checkBoxNode: JQuery) {
	if (checkBoxNode.length !== 1) {
		return null;
	}
	let initialValue = false;
	const initialValueText = $('InitialValue', checkBoxNode).text();
	if (initialValueText === 'true' || initialValueText === 'True') {
		initialValue = true;
	}

	return { initialValue };
}

const InputFieldGenerator = {
	addInputFieldOrLabel(
		fieldNode: JQuery,
		requirementsNode: ParsedRequirementNode,
		workspaceConfiguration: WorkspaceConfiguration
	) {
		let inputNode;
		const rightDiv = $('<div/>').addClass('right');

		// Check for custom credential behaviour
		const customCredential = AuthExtensions.getCredentialHandlerByType(
			requirementsNode.credential.type
		);
		if (customCredential != null) {
			// Handle the auto post case as required
			if (AuthExtensions.isAutoPostAllowed() || !customCredential.isAutoPostCredential) {
				// Either not an auto post credential, or autopost is allowed.
				LabelGenerator.addLabel(fieldNode, requirementsNode);
				inputNode = customCredential.getCredentialTypeMarkup(requirementsNode);

				if (customCredential.isAutoPostCredential) {
					// Update the auto post restriction checks accordingly.
					AuthExtensions.autoPostAddedToForm();

					// Get the input tag and append to the input node
					const autoPostInput = AuthExtensions.getAutoPostInputField(requirementsNode);
					inputNode.append(autoPostInput);

					// Register the callback to run upon rendering the form.
					AuthExtensions.registerAutoPostCallback(
						requirementsNode,
						$.proxy(customCredential.getDataToAutoPost, customCredential),
						workspaceConfiguration
					);
				} else {
					// Flag that a field has been added to the form
					AuthExtensions.fieldAddedToForm();
				}

				rightDiv.append(inputNode);
				fieldNode.append(rightDiv);
			} else {
				reportParsingError(
					'More than one field in a form containing an auto-post credential is not allowed.'
				);
			}
		} else if (requirementsNode.input.radioButton != null) {
			// Continue on default behaviour
			LabelGenerator.addLabel(fieldNode, requirementsNode);
			inputNode = $('<div/>').addClass('radioButtons');
			appendRadioButtons(
				requirementsNode.input.radioButton,
				inputNode,
				requirementsNode.credential.id
			);
			inputNode
				.find('label:first-of-type')
				.after(
					AssistiveTextGenerator.generateInformationIconWithTooltip(requirementsNode)
				);
			rightDiv.append(inputNode);
			fieldNode.append(rightDiv);
			AuthExtensions.fieldAddedToForm();
		} else if (requirementsNode.input.checkBox != null) {
			appendCheckboxAndLabelToRightDiv(requirementsNode, rightDiv);
			rightDiv.append(
				AssistiveTextGenerator.generateInformationIconWithTooltip(requirementsNode)
			);
			fieldNode.append(rightDiv);
			AuthExtensions.fieldAddedToForm();
		} else if (requirementsNode.input.comboBox != null) {
			LabelGenerator.addLabel(fieldNode, requirementsNode);
			inputNode = generateSelectNode(requirementsNode);
			appendOptionNodesToSelectNode(requirementsNode.input.comboBox, inputNode);
			rightDiv.append(inputNode);
			rightDiv.append(
				AssistiveTextGenerator.generateInformationIconWithTooltip(requirementsNode)
			);
			fieldNode.append(rightDiv);
			AuthExtensions.fieldAddedToForm();
		} else if (requirementsNode.input.multiComboBox != null) {
			LabelGenerator.addLabel(fieldNode, requirementsNode);
			inputNode = generateSelectNode(requirementsNode).attr('multiple', 'multiple');
			appendOptionNodesToSelectNode(requirementsNode.input.multiComboBox, inputNode);
			rightDiv.append(inputNode);
			rightDiv.append(
				AssistiveTextGenerator.generateInformationIconWithTooltip(requirementsNode)
			);
			fieldNode.append(rightDiv);
			AuthExtensions.fieldAddedToForm();
		} else if (requirementsNode.input.text != null) {
			LabelGenerator.addLabel(fieldNode, requirementsNode);
			inputNode = InputFieldGenerator.generateTextField(requirementsNode);
			const assistiveText =
				AssistiveTextGenerator.generateInlineAssistiveTextNode(requirementsNode);
			rightDiv.append(assistiveText).append(inputNode);
			fieldNode.append(rightDiv);
			AuthExtensions.fieldAddedToForm();
		} else if (requirementsNode.input.button != null) {
			reportParsingError('Buttons should be parsed elsewhere');
		} else {
			// add image or just the label text if required
			// As standalone labels have no credentials, these are allowed before auto-post credentials.
			LabelGenerator.addStandaloneLabel(fieldNode, requirementsNode);
		}
	},

	generateTextField(requirementsNode: {
		credential: { id: string | number };
		input: {
			text: {
				initialValue: string | number | string[];
				secret: any;
				readOnly: any;
				mustNotBeEmpty: any;
			};
		};
	}) {
		const inputNode = $('<input />')
			.attr('name', requirementsNode.credential.id)
			.attr('id', requirementsNode.credential.id)
			.val(requirementsNode.input.text.initialValue);

		if (requirementsNode.input.text.secret) {
			inputNode.attr('type', 'password');
		} else {
			inputNode.attr('type', 'text');
		}

		if (requirementsNode.input.text.readOnly) {
			inputNode.prop('disabled', true).addClass('alwaysDisabled');
		}

		if (requirementsNode.input.text.mustNotBeEmpty) {
			const checkForText = function (this: Element) {
				const node = $(this);
				if (node.val().length === 0) {
					node.addClass('InvalidField');
				} else {
					node.removeClass('InvalidField');
				}
			};

			inputNode.change(checkForText).keyup(checkForText);
		}

		return inputNode;
	},
};

function generateSelectNode(requirementsNode: any) {
	return $('<select />')
		.attr('id', requirementsNode.credential.id)
		.attr('name', requirementsNode.credential.id);
}

function appendOptionNodesToSelectNode(comboBox: any, inputNode: JQuery) {
	$.each(comboBox.displayValues, function (_index, displayValue) {
		const optionNode = generateOptionNode(displayValue);
		if (
			comboBox.initialSelection === displayValue.value ||
			displayValue.selected === true
		) {
			optionNode.attr('selected', 'selected');
		}
		inputNode.append(optionNode);
	});
}

function generateOptionNode(displayValue: any) {
	const optionNode = $('<option/>');
	optionNode.text(displayValue.display);
	optionNode.attr('value', displayValue.value);
	return optionNode;
}

function appendRadioButtons(radioButton: any, inputNode: JQuery, credentialId: any) {
	$.each(radioButton.displayValues, function (_index, displayValue) {
		const id = displayValue.value + '_id';
		const radioButtonNode = generateRadioButtonNode(displayValue.value, id, credentialId);
		if (radioButton.initialSelection === displayValue.value) {
			radioButtonNode.attr('checked', 'checked');
		}
		const radioButtonParagraph = $('<p/>');

		const labelNode = generateRadioButtonLabel(displayValue.display, id)
			.addClass('radioButton')
			.removeAttr('for');
		labelNode.append(radioButtonNode);
		labelNode.append("<div class='check'></div>");
		radioButtonParagraph.append(labelNode);
		inputNode.append(radioButtonParagraph);
	});
}

function generateRadioButtonNode(value: any, id: string, name: any) {
	return $('<input/>').attr('type', 'radio').val(value).attr('id', id).attr('name', name);
}

function generateRadioButtonLabel(displayText: any, id: string) {
	return $('<label />').text(displayText).attr('for', id);
}

function appendCheckboxAndLabelToRightDiv(
	requirementsNode: ParsedRequirementNode,
	rightDiv: JQuery
) {
	const inputNode = generateCheckboxNode(requirementsNode);
	const labelNode = LabelGenerator.generateInputLabelNode(requirementsNode);

	// Add the input and span to the contents of the label to allow for custom CSS
	labelNode.removeAttr('for');
	labelNode.addClass('checkboxContainer');
	labelNode.append(inputNode);
	labelNode.append("<span class='checkmark'></span>");
	rightDiv.append(labelNode);
	rightDiv.addClass('checkbox');
}

function generateCheckboxNode(requirementsNode: any) {
	const inputNode = $('<input />')
		.attr('id', requirementsNode.credential.id)
		.attr('name', requirementsNode.credential.id)
		.attr('type', 'checkbox')
		.val(requirementsNode.credential.id);

	const defaultValue = requirementsNode.input.checkBox.initialValue;
	if (defaultValue) {
		// move into the checked state once we add the full form to the dom
		inputNode.addClass('CheckMeWhenInDom');
	}

	return inputNode;
}

const AssistiveTextGenerator = {
	informationIconUrl: '',

	// Generates assistive text which appears as if inside the text box
	generateInlineAssistiveTextNode(requirementsNode: { input: { assistiveText: any } }) {
		const assistiveText = requirementsNode.input.assistiveText;
		if (!assistiveText) {
			return '';
		} else {
			return $('<div>' + assistiveText + '</div>').addClass('inlineAssistiveText');
		}
	},

	generateInformationIconWithTooltip(requirementsNode: {
		input: { assistiveText: any };
	}) {
		const assistiveText = requirementsNode.input.assistiveText;
		if (!assistiveText) {
			return '';
		} else {
			return $(
				"<label class='informationAssistive'>" +
					"<img src='" +
					this.informationIconUrl +
					"' title='" +
					assistiveText +
					"' alt='" +
					assistiveText +
					"'/>" +
					"</label><div class='informationAssistiveTooltip'><p>" +
					assistiveText +
					'</p></div>'
			);
		}
	},
};
